import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import {
  Add,
  Delete,
  DragIndicator,
  Edit,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp,
  NavigateNext,
  Refresh,
  Save,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DashboardLayout from '../components/DashboardLayout';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchShiftDistributions,
  upsertItem,
  removeItem,
} from '../store/shiftDistributionsSlice';
import { fetchShifts } from '../store/shiftsSlice';
import { fetchShiftGroups } from '../store/shiftGroupsSlice';
import { fetchEmployeeTags } from '../store/employeeTagsSlice';
import type { EmployeeTag } from '../types/employeeTags';
import { showNotification } from '../store/notificationSlice';
import { apiService } from '../services/api';
import type {
  ShiftDistribution,
  ShiftDistributionShiftType,
  ShiftDistributionElementType,
  SerialItem,
  CreateShiftDistributionPayload,
  PatchShiftDistributionPayload,
} from '../types/shiftDistributions';
import type { Shift } from '../types/shifts';
import type { ShiftGroup } from '../types/shiftGroups';

// ─── Constants ──────────────────────────────────────────────────────────────

const ITEM_HEIGHT = 48;
const TIMELINE_HEIGHT = 480;

// ─── Override item (serial-free, for modal DnD editor) ──────────────────────

interface OverrideItem {
  elementId: string;
  elementType: ShiftDistributionElementType;
  shiftType: ShiftDistributionShiftType;
  shiftId: string;
}

function toOverrides(items: SerialItem[]): OverrideItem[] {
  return [...items]
    .sort((a, b) => a.serial - b.serial)
    .map(({ serial: _serial, ...rest }) => rest);
}

function toSerialItems(overrides: OverrideItem[]): SerialItem[] {
  return overrides.map((o, i) => ({ ...o, serial: i + 1 }));
}

const SHIFT_TYPE_OPTIONS: { value: ShiftDistributionShiftType; label: string }[] = [
  { value: 'shift', label: 'Shift' },
  { value: 'shift_group', label: 'Shift Group' },
];

const ELEMENT_TYPE_OPTIONS: { value: ShiftDistributionElementType; label: string }[] = [
  { value: 'tag', label: 'Tag' },
  { value: 'employee', label: 'Employee' },
];

// ─── Error sanitizer ────────────────────────────────────────────────────────

function sanitizeMsg(msg: string): string {
  return msg;
}

function sanitizeJsonMessage(jm: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(jm).map(([k, v]) => [k, sanitizeMsg(v)]));
}

// ─── Empty helpers ──────────────────────────────────────────────────────────

function emptyCreate(): CreateShiftDistributionPayload {
  return {
    date: new Date().toISOString().split('T')[0],
    defaultShiftId: '',
    defaultShiftType: 'shift',
    shiftApplicationSerial: [],
  };
}

function distributionToPatch(d: ShiftDistribution): PatchShiftDistributionPayload {
  return {
    date: d.date,
    defaultShiftId: d.defaultShiftId,
    defaultShiftType: d.defaultShiftType,
    shiftApplicationSerial: d.shiftApplicationSerial.map((s) => ({ ...s })),
  };
}

// ─── Effective-version helpers ───────────────────────────────────────────────

interface ShiftOption {
  id: string;
  label: string;
}

interface TagOption {
  id: string;   // tagId as string
  label: string;
}

function getTagOptions(tags: EmployeeTag[]): TagOption[] {
  return tags
    .filter((t) => t.tagCode === null)
    .map((t) => ({ id: String(t.tagId), label: t.tagTitle }));
}

function getEffectiveShiftOptions(shifts: Shift[], date: string): ShiftOption[] {
  const map = new Map<string, Shift>();
  for (const s of shifts) {
    if (s.editPointDate <= date) {
      const existing = map.get(s.shiftId);
      if (!existing || s.editPointDate > existing.editPointDate) {
        map.set(s.shiftId, s);
      }
    }
  }
  return Array.from(map.values()).map((s) => ({ id: s.shiftId, label: s.shiftName }));
}

function getEffectiveGroupOptions(groups: ShiftGroup[], date: string): ShiftOption[] {
  const map = new Map<string, ShiftGroup>();
  for (const g of groups) {
    if (g.editPointDate <= date) {
      const existing = map.get(g.shiftGroupId);
      if (!existing || g.editPointDate > existing.editPointDate) {
        map.set(g.shiftGroupId, g);
      }
    }
  }
  return Array.from(map.values()).map((g) => ({ id: g.shiftGroupId, label: g.shiftGroupName }));
}

// ─── ShiftDistributionFormFields ─────────────────────────────────────────────

interface FormFieldsProps {
  form: PatchShiftDistributionPayload;
  onChange: (f: PatchShiftDistributionPayload) => void;
  errors: Record<string, string>;
  showDate?: boolean;
  dateReadOnly?: boolean;
  effectiveDate: string;
  hideSerial?: boolean;
}

const ShiftDistributionFormFields: React.FC<FormFieldsProps> = ({
  form,
  onChange,
  errors,
  showDate,
  dateReadOnly,
  effectiveDate,
  hideSerial,
}) => {
  const allShifts = useAppSelector((state) => state.shifts.shifts);
  const allGroups = useAppSelector((state) => state.shiftGroups.shiftGroups);

  const serial = form.shiftApplicationSerial ?? [];

  const setField = <K extends keyof PatchShiftDistributionPayload>(
    key: K,
    value: PatchShiftDistributionPayload[K]
  ) => {
    onChange({ ...form, [key]: value });
  };

  const setSerialItem = (idx: number, patch: Partial<SerialItem>) => {
    const updated = serial.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    setField('shiftApplicationSerial', updated);
  };

  const addSerialItem = () => {
    const nextSerial = serial.length > 0 ? Math.max(...serial.map((s) => s.serial)) + 1 : 1;
    setField('shiftApplicationSerial', [
      ...serial,
      { serial: nextSerial, elementId: '', elementType: 'tag', shiftType: 'shift', shiftId: '' },
    ]);
  };

  const removeSerialItem = (idx: number) => {
    setField('shiftApplicationSerial', serial.filter((_, i) => i !== idx));
  };

  const options = useMemo(() => {
    if (!effectiveDate) return [];
    if (form.defaultShiftType === 'shift') {
      return getEffectiveShiftOptions(allShifts, effectiveDate);
    }
    return getEffectiveGroupOptions(allGroups, effectiveDate);
  }, [form.defaultShiftType, effectiveDate, allShifts, allGroups]);

  const selectedOption = options.find((o) => o.id === form.defaultShiftId) ?? null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showDate && (
        <TextField
          label="Date"
          type="date"
          size="small"
          fullWidth
          value={form.date ?? ''}
          onChange={(e) => setField('date', e.target.value)}
          error={!!errors['date']}
          helperText={errors['date']}
          disabled={dateReadOnly}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ flex: 1 }} error={!!errors['defaultShiftType']}>
          <InputLabel>Default Shift Type</InputLabel>
          <Select
            label="Default Shift Type"
            value={form.defaultShiftType ?? 'shift'}
            onChange={(e) => {
              onChange({
                ...form,
                defaultShiftType: e.target.value as ShiftDistributionShiftType,
                defaultShiftId: '',
              });
            }}
          >
            {SHIFT_TYPE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
          {errors['defaultShiftType'] && (
            <FormHelperText>{errors['defaultShiftType']}</FormHelperText>
          )}
        </FormControl>

        <Autocomplete
          size="small"
          sx={{ flex: 2 }}
          options={options}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={selectedOption}
          onChange={(_, newValue) => setField('defaultShiftId', newValue?.id ?? '')}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">{option.id}</Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={form.defaultShiftType === 'shift' ? 'Default Shift' : 'Default Shift Group'}
              error={!!errors['defaultShiftId']}
              helperText={errors['defaultShiftId'] ?? (effectiveDate ? undefined : 'Select a date first')}
            />
          )}
          disabled={!effectiveDate}
          noOptionsText={`No effective ${form.defaultShiftType === 'shift' ? 'shifts' : 'shift groups'} on this date`}
        />
      </Box>

      {/* Serial list — hidden in modal; modal uses ModalOverridesEditor instead */}
      {!hideSerial && <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>
            Application Serial
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={addSerialItem}>Add Entry</Button>
        </Box>

        {serial.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
            No entries. Click Add Entry to define overrides.
          </Typography>
        )}

        {serial.map((item, idx) => {
          const p = `shiftApplicationSerial[${idx}]`;
          return (
            <Box
              key={idx}
              sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                p: 1.5, mb: 1.5, position: 'relative',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Entry #{idx + 1}</Typography>
                <Tooltip title="Remove entry">
                  <IconButton size="small" color="error" onClick={() => removeSerialItem(idx)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                  label="Serial"
                  type="number"
                  size="small"
                  fullWidth
                  value={item.serial}
                  onChange={(e) => setSerialItem(idx, { serial: Number(e.target.value) })}
                  error={!!errors[`${p}.serial`]}
                  helperText={errors[`${p}.serial`]}
                  slotProps={{ htmlInput: { min: 1 } }}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }} error={!!errors[`${p}.elementType`]}>
                    <InputLabel>Element Type</InputLabel>
                    <Select
                      label="Element Type"
                      value={item.elementType}
                      onChange={(e) => setSerialItem(idx, { elementType: e.target.value as ShiftDistributionElementType })}
                    >
                      {ELEMENT_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                    {errors[`${p}.elementType`] && <FormHelperText>{errors[`${p}.elementType`]}</FormHelperText>}
                  </FormControl>

                  <TextField
                    label="Element ID"
                    size="small"
                    sx={{ flex: 2 }}
                    value={item.elementId}
                    onChange={(e) => setSerialItem(idx, { elementId: e.target.value })}
                    error={!!errors[`${p}.elementId`]}
                    helperText={errors[`${p}.elementId`] ?? (item.elementType === 'tag' ? 'Tag integer ID' : 'Employee ID')}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ flex: 1 }} error={!!errors[`${p}.shiftType`]}>
                    <InputLabel>Shift Type</InputLabel>
                    <Select
                      label="Shift Type"
                      value={item.shiftType}
                      onChange={(e) => setSerialItem(idx, { shiftType: e.target.value as ShiftDistributionShiftType })}
                    >
                      {SHIFT_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                    {errors[`${p}.shiftType`] && <FormHelperText>{errors[`${p}.shiftType`]}</FormHelperText>}
                  </FormControl>

                  <TextField
                    label="Shift ID"
                    size="small"
                    sx={{ flex: 2 }}
                    value={item.shiftId}
                    onChange={(e) => setSerialItem(idx, { shiftId: e.target.value })}
                    error={!!errors[`${p}.shiftId`]}
                    helperText={errors[`${p}.shiftId`] ?? 'UUID of the shift or shift group'}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>}
    </Box>
  );
};

// ─── SortableOverrideItem ────────────────────────────────────────────────────

interface SortableOverrideItemProps {
  id: string;
  idx: number;
  total: number;
  item: OverrideItem;
  expanded: boolean;
  onToggle: () => void;
  errors: Record<string, string>;
  onChange: (patch: Partial<OverrideItem>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  itemRef: (el: HTMLDivElement | null) => void;
  tagOptions: TagOption[];
  shiftOptions: ShiftOption[];
  groupOptions: ShiftOption[];
}

const SortableOverrideItem: React.FC<SortableOverrideItemProps> = ({
  id,
  idx,
  total,
  item,
  expanded,
  onToggle,
  errors,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
  itemRef,
  tagOptions,
  shiftOptions,
  groupOptions,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const p = `shiftApplicationSerial[${idx}]`;

  const elementLabel = item.elementType === 'tag' ? 'Tag' : 'Employee Name';
  const shiftLabel = item.shiftType === 'shift_group' ? 'Shift Group Name' : 'Shift Name';

  const selectedElement = (item.elementType === 'tag' ? tagOptions : []).find((o) => o.id === item.elementId) ?? null;
  const selectedShift = (item.shiftType === 'shift' ? shiftOptions : groupOptions).find((o) => o.id === item.shiftId) ?? null;

  const summaryText = (() => {
    if (!item.elementId && !item.shiftId) return 'New override';
    const elTypeLabel = item.elementType === 'tag' ? 'Tag' : 'Employee';
    const elPart = selectedElement ? `${elTypeLabel} | ${selectedElement.label}` : elTypeLabel;
    const shTypeLabel = item.shiftType === 'shift_group' ? 'Shift Group' : 'Shift';
    const shPart = selectedShift ? ` ${shTypeLabel} | ${selectedShift.label}` : '';
    return `${elPart} →${shPart}`;
  })();

  return (
    <Box ref={(el) => { const node = el as HTMLDivElement | null; setNodeRef(node); itemRef(node); }} style={style} sx={{ mb: 1 }}>
      <Accordion disableGutters expanded={expanded} onChange={onToggle}>
        <AccordionSummary
          {...listeners}
          {...attributes}
          sx={{
            cursor: 'grab',
            '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 0.5, minWidth: 0 },
          }}
        >
          <DragIndicator fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {summaryText}
          </Typography>
          {expanded ? <ExpandMore sx={{ transform: 'rotate(180deg)', color: 'text.secondary', flexShrink: 0 }} /> : <ExpandMore sx={{ color: 'text.secondary', flexShrink: 0 }} />}
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={idx === 0}>
            <KeyboardArrowUp fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={idx === total - 1}>
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
            <Delete fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: '5px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }} error={!!errors[`${p}.elementType`]}>
                <InputLabel>Element Type</InputLabel>
                <Select
                  label="Element Type"
                  value={item.elementType}
                  onChange={(e) => onChange({ elementType: e.target.value as ShiftDistributionElementType, elementId: '' })}
                >
                  {ELEMENT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                {errors[`${p}.elementType`] && <FormHelperText>{errors[`${p}.elementType`]}</FormHelperText>}
              </FormControl>
              <Autocomplete
                size="small"
                sx={{ flex: 2 }}
                options={item.elementType === 'tag' ? tagOptions : []}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={selectedElement}
                onChange={(_, newValue) => onChange({ elementId: newValue?.id ?? '' })}
                noOptionsText={item.elementType === 'employee' ? 'No employees available' : 'No tags found'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={elementLabel}
                    error={!!errors[`${p}.elementId`]}
                    helperText={errors[`${p}.elementId`]}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }} error={!!errors[`${p}.shiftType`]}>
                <InputLabel>Shift Type</InputLabel>
                <Select
                  label="Shift Type"
                  value={item.shiftType}
                  onChange={(e) => onChange({ shiftType: e.target.value as ShiftDistributionShiftType, shiftId: '' })}
                >
                  {SHIFT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
                {errors[`${p}.shiftType`] && <FormHelperText>{errors[`${p}.shiftType`]}</FormHelperText>}
              </FormControl>
              <Autocomplete
                size="small"
                sx={{ flex: 2 }}
                options={item.shiftType === 'shift' ? shiftOptions : groupOptions}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={selectedShift}
                onChange={(_, newValue) => onChange({ shiftId: newValue?.id ?? '' })}
                noOptionsText="No options"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={shiftLabel}
                    error={!!errors[`${p}.shiftId`]}
                    helperText={errors[`${p}.shiftId`]}
                  />
                )}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// ─── ModalOverridesEditor ────────────────────────────────────────────────────

interface ModalOverridesEditorProps {
  serialItems: SerialItem[];
  onChange: (items: SerialItem[]) => void;
  errors: Record<string, string>;
  effectiveDate: string;
}

const ModalOverridesEditor: React.FC<ModalOverridesEditorProps> = ({ serialItems, onChange, errors, effectiveDate }) => {
  const overrides = toOverrides(serialItems);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const allTags = useAppSelector((state) => state.employeeTags.tags);
  const allShifts = useAppSelector((state) => state.shifts.shifts);
  const allGroups = useAppSelector((state) => state.shiftGroups.shiftGroups);

  const tagOptions = useMemo(() => getTagOptions(allTags), [allTags]);
  const shiftOptions = useMemo(() => getEffectiveShiftOptions(allShifts, effectiveDate), [allShifts, effectiveDate]);
  const groupOptions = useMemo(() => getEffectiveGroupOptions(allGroups, effectiveDate), [allGroups, effectiveDate]);

  const commit = (updated: OverrideItem[]) => onChange(toSerialItems(updated));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setExpandedIdx(null);
    if (!over || active.id === over.id) return;
    const oldIdx = Number(active.id);
    const newIdx = Number(over.id);
    commit(arrayMove(overrides, oldIdx, newIdx));
  };

  const addOverride = () => {
    const newIdx = overrides.length;
    commit([...overrides, { elementId: '', elementType: 'tag', shiftType: 'shift', shiftId: '' }]);
    setExpandedIdx(newIdx);
  };

  useEffect(() => {
    if (expandedIdx === null) return;
    const el = itemRefs.current[expandedIdx];
    const container = scrollContainerRef.current;
    if (!el || !container) return;
    const elTop = el.offsetTop;
    const elBottom = elTop + el.offsetHeight;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;
    if (elTop < visibleTop) {
      container.scrollTo({ top: elTop, behavior: 'smooth' });
    } else if (elBottom > visibleBottom) {
      container.scrollTo({ top: elBottom - container.clientHeight, behavior: 'smooth' });
    }
  }, [expandedIdx]);

  const updateOverride = (idx: number, patch: Partial<OverrideItem>) => {
    commit(overrides.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };

  const moveOverride = (idx: number, dir: -1 | 1) => {
    const next = idx + dir;
    if (next < 0 || next >= overrides.length) return;
    const updated = [...overrides];
    [updated[idx], updated[next]] = [updated[next], updated[idx]];
    commit(updated);
  };

  const removeOverride = (idx: number) => {
    commit(overrides.filter((_, i) => i !== idx));
  };

  const ids = overrides.map((_, i) => String(i));

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>
          Shift Overrides
        </Typography>
        <Tooltip title="Add override">
          <IconButton size="small" onClick={addOverride}><Add fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          height: 'calc(6 * 52px)',
          overflowY: 'auto',
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'action.selected',
          borderRadius: 1,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {overrides.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', m: 'auto' }}>
            No overrides defined.
          </Typography>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {overrides.map((item, idx) => (
                <SortableOverrideItem
                  key={String(idx)}
                  id={String(idx)}
                  idx={idx}
                  total={overrides.length}
                  item={item}
                  expanded={expandedIdx === idx}
                  onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                  errors={errors}
                  onChange={(patch) => updateOverride(idx, patch)}
                  onMoveUp={() => moveOverride(idx, -1)}
                  onMoveDown={() => moveOverride(idx, 1)}
                  onRemove={() => removeOverride(idx)}
                  itemRef={(el) => { itemRefs.current[idx] = el; }}
                  tagOptions={tagOptions}
                  shiftOptions={shiftOptions}
                  groupOptions={groupOptions}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </Box>
  );
};

// ─── DistributionInfoPanel ───────────────────────────────────────────────────

function resolveEffectiveShifts(shifts: Shift[], date: string): Map<string, Shift> {
  const map = new Map<string, Shift>();
  for (const s of shifts) {
    if (s.editPointDate <= date) {
      const ex = map.get(s.shiftId);
      if (!ex || s.editPointDate > ex.editPointDate) map.set(s.shiftId, s);
    }
  }
  return map;
}

function resolveEffectiveGroups(groups: ShiftGroup[], date: string): Map<string, ShiftGroup> {
  const map = new Map<string, ShiftGroup>();
  for (const g of groups) {
    if (g.editPointDate <= date) {
      const ex = map.get(g.shiftGroupId);
      if (!ex || g.editPointDate > ex.editPointDate) map.set(g.shiftGroupId, g);
    }
  }
  return map;
}

const fmt = (t: string) => t.slice(0, 5);
const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

interface InfoPanelProps {
  distribution: ShiftDistribution;
  date: string;
}

const DistributionInfoPanel: React.FC<InfoPanelProps> = ({ distribution, date }) => {
  const allShifts = useAppSelector((s) => s.shifts.shifts);
  const allGroups = useAppSelector((s) => s.shiftGroups.shiftGroups);
  const allTags   = useAppSelector((s) => s.employeeTags.tags);

  const shiftMap = useMemo(() => resolveEffectiveShifts(allShifts, date), [allShifts, date]);
  const groupMap = useMemo(() => resolveEffectiveGroups(allGroups, date), [allGroups, date]);

  const scopeLabel = (item: SerialItem) => {
    if (item.elementType === 'tag') {
      const tag = allTags.find((t) => String(t.tagId) === item.elementId);
      return `Tag: ${tag?.tagTitle ?? item.elementId}`;
    }
    return `Employee: ${item.elementId}`;
  };

  type Row = { priority: string; scope: string; shiftType: ShiftDistributionShiftType; shiftId: string };
  const rows: Row[] = [
    { priority: 'Default', scope: '—', shiftType: distribution.defaultShiftType, shiftId: distribution.defaultShiftId },
    ...(distribution.shiftApplicationSerial ?? []).map((s, i) => ({
      priority: String(i + 1),
      scope: scopeLabel(s),
      shiftType: s.shiftType,
      shiftId: s.shiftId,
    })),
  ];

  const ScheduleKV: React.FC<{ shift: Shift }> = ({ shift }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 3, rowGap: 0.5, mt: 1 }}>
      <Typography variant="body2" color="text.secondary">Shift</Typography>
      <Typography variant="body2">{fmt(shift.shiftStartTime)} – {fmt(shift.shiftEndTime)}</Typography>
      <Typography variant="body2" color="text.secondary">Work</Typography>
      <Typography variant="body2">{fmt(shift.workStartTime)} – {fmt(shift.workEndTime)}</Typography>
      <Typography variant="body2" color="text.secondary">Holiday</Typography>
      <Typography variant="body2">{capitalize(shift.weeklyHoliday)}</Typography>
      <Typography variant="body2" color="text.secondary">Breaks</Typography>
      {shift.breaks.length === 0
        ? <Typography variant="body2" color="text.disabled">—</Typography>
        : <Box>
            {shift.breaks.map((b, bi) => (
              <Typography key={bi} variant="body2">{b.breakName}: {fmt(b.breakStartTime)} – {fmt(b.breakEndTime)}</Typography>
            ))}
          </Box>
      }
    </Box>
  );

  const blueShadeScale = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

  return (
    <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Cards */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto', display: 'flex', flexWrap: 'wrap', gap: 2, alignContent: 'flex-start' }}>
      {rows.map((row, i) => {
        const isDefault = row.priority === 'Default';

        const blueShades = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
        const shadeIdx = rows.length <= 1 ? blueShades.length - 1 : Math.round((i / (rows.length - 1)) * (blueShades.length - 1));
        const priorityColor = blue[blueShades[shadeIdx]];

        const cardHeader = (typeLabel: string, typeColor: 'primary' | 'secondary', name: React.ReactNode) => (
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: isDefault ? 'action.selected' : 'inherit' }}>
            <Chip label={typeLabel} size="small" color={typeColor} variant="outlined" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
            <Box sx={{ ml: 'auto', width: 16, height: 24, borderRadius: 0.5, bgcolor: priorityColor, flexShrink: 0 }} />
          </Box>
        );

        const cardFooter = (() => {
          const [label, ...rest] = isDefault ? ['Default', ''] : row.scope.split(': ');
          return (
            <Box sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>Application Scope</Typography>
              <Typography variant="caption" color="text.disabled">|</Typography>
              <Typography variant="caption" color="text.secondary">
                {isDefault
                  ? <Box component="span" sx={{ fontWeight: 700 }}>Default</Box>
                  : <><Box component="span" sx={{ fontWeight: 700 }}>{label}:</Box>{' '}{rest.join(': ')}</>
                }
              </Typography>
            </Box>
          );
        })();

        if (row.shiftType === 'shift') {
          const shift = shiftMap.get(row.shiftId);
          return (
            <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, minWidth: 220, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              {cardHeader('Shift', 'primary', shift?.shiftName ?? <Box component="span" sx={{ color: 'error.main' }}>{row.shiftId || 'Not set'}</Box>)}
              <Box sx={{ px: 1.5, py: 1, flex: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', letterSpacing: 0.5 }}>Schedule</Typography>
                {shift ? <ScheduleKV shift={shift} /> : <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>No shift data</Typography>}
              </Box>
              {cardFooter}
            </Box>
          );
        } else {
          const group = groupMap.get(row.shiftId);
          const groupShifts = (group?.shiftIdList ?? []).map((id) => shiftMap.get(id)).filter(Boolean) as Shift[];
          return (
            <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, minWidth: 220, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              {cardHeader('Group', 'secondary', group?.shiftGroupName ?? <Box component="span" sx={{ color: 'error.main' }}>{row.shiftId || 'Not set'}</Box>)}
              <Box sx={{ flex: 1 }}>
                {groupShifts.length === 0
                  ? <Box sx={{ px: 1.5, py: 1 }}><Typography variant="body2" color="text.disabled">No shifts resolved</Typography></Box>
                  : groupShifts.map((shift, si) => (
                    <Box key={si} sx={{ px: 1.5, py: 1, borderTop: si === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.disabled', letterSpacing: 0.5 }}>
                        {shift.shiftName}
                      </Typography>
                      <ScheduleKV shift={shift} />
                    </Box>
                  ))
                }
              </Box>
              {cardFooter}
            </Box>
          );
        }
      })}
      </Box>

      {/* Legend */}
      <Box sx={{ px: 2, pt: 1, pb: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Priority</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.disabled">Low</Typography>
            {blueShadeScale.map((shade) => (
              <Box key={shade} sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: blue[shade] }} />
            ))}
            <Typography variant="caption" color="text.disabled">High</Typography>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          If an employee belongs to multiple application scopes, the highest priority scope will be applied.
        </Typography>
      </Box>
    </Box>
  );
};

// ─── ShiftDistributions (main page) ─────────────────────────────────────────

const ShiftDistributions: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, isLoading, error } = useAppSelector((state) => state.shiftDistributions);

  const [selectedDate, setSelectedDate] = useState('');
  const [tlScrollOffset, setTlScrollOffset] = useState(0);

  // New date dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState<CreateShiftDistributionPayload>(emptyCreate());
  const [newErrors, setNewErrors] = useState<Record<string, string>>({});
  const [newLoading, setNewLoading] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editDate, setEditDate] = useState('');
  const [editForm, setEditForm] = useState<PatchShiftDistributionPayload | null>(null);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editLoading, setEditLoading] = useState(false);


  // Sorted newest-first for left panel
  const sortedItems = [...items].sort((a, b) => b.date.localeCompare(a.date));

  const handleRefresh = () => {
    dispatch(fetchShiftDistributions());
    dispatch(fetchShifts());
    dispatch(fetchShiftGroups());
    dispatch(fetchEmployeeTags());
  };

  const handleRefreshForeignItems = () => {
    dispatch(fetchShifts());
    dispatch(fetchShiftGroups());
    dispatch(fetchEmployeeTags());
  };

  const openNewDialog = () => {
    setNewForm(emptyCreate());
    setNewErrors({});
    setNewOpen(true);
  };

  const handleCreate = async () => {
    setNewLoading(true);
    setNewErrors({});
    try {
      const res = await apiService.createShiftDistribution(newForm);
      dispatch(upsertItem(res.data));
      setSelectedDate(res.data.date);
      setNewOpen(false);
      dispatch(showNotification({ message: 'Distribution created', severity: 'success' }));
    } catch (err: any) {
      if (err?.jsonMessage) {
        setNewErrors(sanitizeJsonMessage(err.jsonMessage));
      } else {
        dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to create distribution'), severity: 'error' }));
      }
    } finally {
      setNewLoading(false);
    }
  };

  const openEditDialog = (date: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDate(date);
    const found = items.find((d) => d.date === date);
    if (!found) return;
    setEditDate(date);
    setEditForm(distributionToPatch(found));
    setEditErrors({});
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editForm) return;
    setEditLoading(true);
    setEditErrors({});
    try {
      const res = await apiService.updateShiftDistribution({ ...editForm, date: editDate });
      dispatch(upsertItem(res.data));
      setEditOpen(false);
      dispatch(showNotification({ message: 'Distribution saved', severity: 'success' }));
    } catch (err: any) {
      if (err?.jsonMessage) {
        setEditErrors(sanitizeJsonMessage(err.jsonMessage));
      } else {
        dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to save distribution'), severity: 'error' }));
      }
    } finally {
      setEditLoading(false);
    }
  };

  const openDelete = (date: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(date);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await apiService.deleteShiftDistribution(deleteTarget);
      dispatch(removeItem(deleteTarget));
      if (selectedDate === deleteTarget) {
        setSelectedDate('');
      }
      setDeleteOpen(false);
      dispatch(showNotification({ message: 'Distribution deleted', severity: 'success' }));
    } catch (err: any) {
      dispatch(showNotification({ message: sanitizeMsg(err?.message ?? 'Failed to delete distribution'), severity: 'error' }));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Box>
        {/* Breadcrumbs */}
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Scheduling</Typography>
          <Typography variant="body2" color="text.primary" fontWeight={600}>Shift Distributions</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Shift Distributions</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh} disabled={isLoading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <Box sx={{
            display: 'flex',
            height: TIMELINE_HEIGHT + 80,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}>
            {/* ── Left panel: date list ── */}
            <Box sx={{ width: 286, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', p: 1.5, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary' }}>
                  Dates
                </Typography>
                <Tooltip title="New Date">
                  <IconButton size="small" onClick={openNewDialog}><Add fontSize="small" /></IconButton>
                </Tooltip>
              </Box>

              {/* Scrollable list with fade effects */}
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <Box
                  onScroll={(e) => setTlScrollOffset(e.currentTarget.scrollTop)}
                  sx={{
                    height: '100%', overflowY: 'auto', userSelect: 'none',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                  }}
                >
                  {/* Upward arrow at top of timeline line */}
                  <Box sx={{
                    position: 'sticky', top: 0, left: 16, transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                    borderBottom: '8px solid', borderBottomColor: '#e0e0e0', zIndex: 0,
                  }} />

                  {sortedItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No distributions yet.
                    </Typography>
                  ) : sortedItems.map((d) => {
                    const isSelected = d.date === selectedDate;
                    return (
                      <Box
                        key={d.date}
                        onClick={() => setSelectedDate(d.date)}
                        sx={{ display: 'flex', alignItems: 'center', height: ITEM_HEIGHT, cursor: 'pointer', '&:hover .date-label': { color: 'primary.main' } }}
                      >
                        {/* Line + dot */}
                        <Box sx={{ width: 32, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                          <Box sx={{
                            width: isSelected ? 16 : 10, height: isSelected ? 16 : 10,
                            borderRadius: '50%', bgcolor: isSelected ? 'primary.main' : '#bdbdbd',
                            zIndex: 1, transition: 'all 0.15s',
                            border: isSelected ? '2px solid' : 'none', borderColor: 'primary.main',
                          }} />
                        </Box>
                        {/* Date label */}
                        <Typography
                          className="date-label"
                          variant="body2"
                          sx={{ flex: 1, fontWeight: isSelected ? 700 : 400, color: isSelected ? 'primary.main' : 'text.primary', transition: 'color 0.15s', fontSize: '0.82rem' }}
                        >
                          {d.date}
                        </Typography>
                        {/* Edit */}
                        <Tooltip title="Edit this distribution">
                          <IconButton size="small" sx={{ mr: '2px' }} onClick={(e) => openEditDialog(d.date, e)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {/* Delete */}
                        <Tooltip title="Delete this distribution">
                          <IconButton size="small" color="error" sx={{ mr: '4px' }} onClick={(e) => openDelete(d.date, e)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  })}

                  {/* Trailing line */}
                  {sortedItems.length > 0 && (
                    <Box sx={{ width: 32, height: 20, position: 'relative', flexShrink: 0 }}>
                      <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 2, bgcolor: '#e0e0e0', transform: 'translateX(-50%)' }} />
                    </Box>
                  )}
                </Box>

                {/* Top fade */}
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: Math.min(1, tlScrollOffset / 40), transition: 'opacity 0.15s',
                }} />
                {/* Bottom fade */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, pointerEvents: 'none',
                  background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))',
                  opacity: Math.min(1, (Math.max(0, sortedItems.length * ITEM_HEIGHT - TIMELINE_HEIGHT) - tlScrollOffset) / 40),
                  transition: 'opacity 0.15s',
                }} />
              </Box>
            </Box>

            {/* ── Right panel: info ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {!selectedDate ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Select a date to view its distribution</Typography>
                </Box>
              ) : (() => {
                const dist = items.find((d) => d.date === selectedDate);
                return (
                  <>
                    {/* Header */}
                    <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        <Box component="span" sx={{ color: 'primary.main' }}>Distribution</Box>
                        {' | '}{selectedDate}
                      </Typography>
                      <Tooltip title="Refresh shifts and groups">
                        <IconButton size="small" onClick={handleRefreshForeignItems}>
                          <Refresh fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Info table */}
                    {dist
                      ? <DistributionInfoPanel distribution={dist} date={selectedDate} />
                      : <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography color="text.secondary">Loading…</Typography>
                        </Box>
                    }
                  </>
                );
              })()}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── New Date Dialog ── */}
      <Dialog open={newOpen} onClose={(_, reason) => { if (reason !== 'backdropClick') setNewOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          New Distribution
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefreshForeignItems}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <ShiftDistributionFormFields
            form={newForm}
            onChange={(f) => setNewForm(f as CreateShiftDistributionPayload)}
            errors={newErrors}
            showDate
            effectiveDate={newForm.date}
            hideSerial
          />
          <ModalOverridesEditor
            serialItems={newForm.shiftApplicationSerial}
            onChange={(items) => setNewForm((f) => ({ ...f, shiftApplicationSerial: items }))}
            errors={newErrors}
            effectiveDate={newForm.date}
          />
        </DialogContent>
        <DialogActions sx={{ p: '20px' }}>
          <Button onClick={() => setNewOpen(false)} disabled={newLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={newLoading}
            startIcon={newLoading ? <CircularProgress size={14} /> : <Save />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Distribution Dialog ── */}
      <Dialog open={editOpen} onClose={(_, reason) => { if (reason !== 'backdropClick') setEditOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Edit Distribution
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefreshForeignItems}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <>
              <ShiftDistributionFormFields
                form={editForm}
                onChange={(f) => setEditForm(f)}
                errors={editErrors}
                showDate
                dateReadOnly
                effectiveDate={editDate}
                hideSerial
              />
              <ModalOverridesEditor
                serialItems={editForm.shiftApplicationSerial ?? []}
                onChange={(items) => setEditForm((f) => f ? { ...f, shiftApplicationSerial: items } : f)}
                errors={editErrors}
                effectiveDate={editDate}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '20px' }}>
          <Button onClick={() => setEditOpen(false)} disabled={editLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={14} /> : <Save />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Dialog ── */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Distribution</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the distribution for <strong>{deleteTarget}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={14} /> : <Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ShiftDistributions;
